import { CTACard } from '../shared-components/CTACard';
import { AccordionStep, ProgressAccordionStepProps } from '../shared-components/AccordionStep';
import { ProgressStepStatusEnum, SimplifiedUserRole } from '@platform/client/schemas';
import { useState } from 'react';
import { ActionUpdateModal } from '../modal-components/ActionUpdateModal';
import cx from 'classix';
import { HubExternalLink } from '../shared-components/HubExternalLink';

export const Action = ({
  isTeam,
  userType,
  status,
  planningGuideUrl,
  projectId,
}: ProgressAccordionStepProps & { planningGuideUrl?: string | null; projectId?: number }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const headerContent = getHeaderContent(userType, isTeam);
  return (
    <>
      <AccordionStep title='Action' status={status}>
        <p>📝 Your project is officially in progress!</p>
        <p>{headerContent}</p>
        <p className='text-md font-semibold'>Tips as you collaborate</p>
        <ActionList
          userType={userType}
          planningGuideUrl={planningGuideUrl}
          isTeam={isTeam}
          activeLinks={
            status === ProgressStepStatusEnum.active_in_progress ||
            status === ProgressStepStatusEnum.success
          }
        />
        <CTACard
          title='Update us on your progress'
          description={getCTAText(userType, isTeam)}
          ctaButtonProps={{
            size: 'sm',
            children: 'Provide update',
            variant: 'primary',
            onPress: () => setModalIsOpen(true),
            isDisabled: status !== ProgressStepStatusEnum.active_in_progress,
          }}
        />
      </AccordionStep>
      {projectId && (
        <ActionUpdateModal
          onClose={() => setModalIsOpen(false)}
          projectId={projectId}
          isOpen={modalIsOpen}
          userType={userType}
          isTeam={isTeam ?? false}
        />
      )}
    </>
  );
};

const getHeaderContent = (userType: SimplifiedUserRole, isTeam?: boolean) => {
  if (userType === SimplifiedUserRole.PROFESSIONAL) {
    return 'Now is when your skills start creating real impact. Put your plan into action, keep in touch regularly, and use this project to apply your strengths in a new context.';
  } else {
    return `This is the stage where the work starts to make a difference. Keeping communication open helps your ${isTeam ? 'team of professionals' : 'professional'} stay aligned with your priorities and deliver outcomes that matter to your team.`;
  }
};

const ActionList = ({
  userType,
  planningGuideUrl,
  isTeam,
  activeLinks = true,
}: {
  userType: SimplifiedUserRole;
  planningGuideUrl?: string | null;
  isTeam?: boolean;
  activeLinks?: boolean;
}) => {
  return (
    <ul className={cx('list-disc px-4', activeLinks && 'blue-links')}>
      <li>
        Refer back to your&nbsp;
        <HubExternalLink url={planningGuideUrl} active={activeLinks}>
          Planning Guide
        </HubExternalLink>
        &nbsp;throughout the project to help you prioritize and stay focused on your deliverables.
      </li>
      {userType === SimplifiedUserRole.REPRESENTATIVE && (
        <li>
          Schedule regular check-ins to ask questions, clarify priorities, and celebrate milestones.
        </li>
      )}
      {userType === SimplifiedUserRole.PROFESSIONAL && isTeam && (
        <li>
          Schedule regular check-ins with your team and representative to share progress, ask
          questions, get feedback, and plan next steps.
        </li>
      )}
      {userType === SimplifiedUserRole.PROFESSIONAL && !isTeam && (
        <li>
          Schedule regular check-ins to share progress, ask questions, get feedback, and plan next
          steps.
        </li>
      )}
      <li>
        Stay flexible. Adjust your approach as needed — projects often evolve as you learn more.
      </li>
      <li>
        Provide an update here once your project is complete to move to the final step. You can also
        share an update if you need help or if something has changed.
      </li>
    </ul>
  );
};

const getCTAText = (userType: SimplifiedUserRole, isTeam?: boolean) => {
  if (userType === SimplifiedUserRole.PROFESSIONAL) {
    return `${isTeam ? 'Anyone on your team can provide an update.' : ''} Let us know whether things are going smoothly, you're facing delays, or you've completed your deliverables.`;
  } else {
    return `Let us know whether things are going smoothly, you're facing delays, or your ${isTeam ? 'team of professionals' : 'professional'} completed your deliverables.`;
  }
};
